import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import Header from '../../components/ui/Header';
import MobileNav from '../../components/ui/MobileNav';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import {
  getUserCampaignLinks,
  createCampaignLink,
  updateCampaignLink,
  deleteCampaignLink,
  generateSlug,
} from '../../services/musicLinkService';

const BASE_URL = 'https://ozymakers.com/go/';

const EMPTY_FORM = {
  title: '',
  artistName: '',
  coverImageUrl: '',
  spotifyUrl: '',
  appleMusicUrl: '',
  youtubeMusicUrl: '',
  tiktokSoundUrl: '',
  campaignSlug: '',
};

const CampaignLinkManager = () => {
  const { user } = useAuth();
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingLink, setEditingLink] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [copiedId, setCopiedId] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  useEffect(() => {
    if (user?.id) loadLinks();
  }, [user?.id]);

  const loadLinks = async () => {
    setLoading(true);
    const { data } = await getUserCampaignLinks(user?.id);
    setLinks(data || []);
    setLoading(false);
  };

  const handleOpenCreate = () => {
    setEditingLink(null);
    setForm(EMPTY_FORM);
    setError('');
    setShowForm(true);
  };

  const handleOpenEdit = (link) => {
    setEditingLink(link);
    setForm({
      title: link?.title || '',
      artistName: link?.artist_name || '',
      coverImageUrl: link?.cover_image_url || '',
      spotifyUrl: link?.spotify_url || '',
      appleMusicUrl: link?.apple_music_url || '',
      youtubeMusicUrl: link?.youtube_music_url || '',
      tiktokSoundUrl: link?.tiktok_sound_url || '',
      campaignSlug: link?.campaign_slug || '',
    });
    setError('');
    setShowForm(true);
  };

  const handleChange = (e) => {
    const { name, value } = e?.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!form?.title?.trim()) {
      setError('Title is required.');
      return;
    }
    setSaving(true);
    setError('');

    if (editingLink) {
      const { error: err } = await updateCampaignLink(editingLink?.id, user?.id, {
        title: form?.title,
        artistName: form?.artistName,
        coverImageUrl: form?.coverImageUrl,
        spotifyUrl: form?.spotifyUrl,
        appleMusicUrl: form?.appleMusicUrl,
        youtubeMusicUrl: form?.youtubeMusicUrl,
        tiktokSoundUrl: form?.tiktokSoundUrl,
        isActive: true,
      });
      if (err) {
        setError(err?.message || 'Failed to update link.');
      } else {
        setShowForm(false);
        loadLinks();
      }
    } else {
      const slug = form?.campaignSlug?.trim() || generateSlug(form?.title);
      const { error: err } = await createCampaignLink(user?.id, { ...form, campaignSlug: slug });
      if (err) {
        setError(err?.message || 'Failed to create link. Slug may already exist.');
      } else {
        setShowForm(false);
        loadLinks();
      }
    }
    setSaving(false);
  };

  const handleCopy = (slug) => {
    navigator.clipboard?.writeText(BASE_URL + slug);
    setCopiedId(slug);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleDelete = async (linkId) => {
    const { error: err } = await deleteCampaignLink(linkId, user?.id);
    if (!err) {
      setLinks(prev => prev?.filter(l => l?.id !== linkId));
    }
    setDeleteConfirm(null);
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pb-24 lg:pb-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-foreground mb-1">Music Links</h1>
            <p className="text-muted-foreground text-sm">Generate smart tracking links for your campaigns</p>
          </div>
          <Button onClick={handleOpenCreate} className="flex items-center gap-2">
            <Icon name="Plus" size={18} />
            New Link
          </Button>
        </div>

        {/* Create / Edit Form */}
        {showForm && (
          <div className="bg-card border border-border rounded-2xl p-6 mb-8 shadow-sm">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold text-foreground">
                {editingLink ? 'Edit Link' : 'Create New Link'}
              </h2>
              <button onClick={() => setShowForm(false)} className="text-muted-foreground hover:text-foreground transition-colors">
                <Icon name="X" size={20} />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Track Title *</label>
                  <input
                    name="title"
                    value={form?.title}
                    onChange={handleChange}
                    placeholder="e.g. My New Single"
                    className="w-full px-4 py-2.5 bg-background border border-border rounded-xl text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Artist Name</label>
                  <input
                    name="artistName"
                    value={form?.artistName}
                    onChange={handleChange}
                    placeholder="e.g. OzyMakers"
                    className="w-full px-4 py-2.5 bg-background border border-border rounded-xl text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                  />
                </div>
              </div>

              {!editingLink && (
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    Custom Slug <span className="text-muted-foreground font-normal">(optional)</span>
                  </label>
                  <div className="flex items-center gap-2">
                    <span className="text-muted-foreground text-sm whitespace-nowrap">ozymakers.com/go/</span>
                    <input
                      name="campaignSlug"
                      value={form?.campaignSlug}
                      onChange={handleChange}
                      placeholder="my-track-2026"
                      className="flex-1 px-4 py-2.5 bg-background border border-border rounded-xl text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                    />
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Cover Image URL</label>
                <input
                  name="coverImageUrl"
                  value={form?.coverImageUrl}
                  onChange={handleChange}
                  placeholder="https://..."
                  className="w-full px-4 py-2.5 bg-background border border-border rounded-xl text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                />
              </div>

              <div className="border-t border-border pt-4">
                <p className="text-sm font-medium text-foreground mb-3">Streaming Links</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {[
                    { name: 'spotifyUrl', label: 'Spotify URL', placeholder: 'https://open.spotify.com/...' },
                    { name: 'appleMusicUrl', label: 'Apple Music URL', placeholder: 'https://music.apple.com/...' },
                    { name: 'youtubeMusicUrl', label: 'YouTube Music URL', placeholder: 'https://music.youtube.com/...' },
                    { name: 'tiktokSoundUrl', label: 'TikTok Sound URL', placeholder: 'https://www.tiktok.com/music/...' },
                  ]?.map(field => (
                    <div key={field?.name}>
                      <label className="block text-xs font-medium text-muted-foreground mb-1">{field?.label}</label>
                      <input
                        name={field?.name}
                        value={form?.[field?.name]}
                        onChange={handleChange}
                        placeholder={field?.placeholder}
                        className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                      />
                    </div>
                  ))}
                </div>
              </div>

              {error && (
                <p className="text-sm text-red-500 bg-red-50 dark:bg-red-900/20 px-3 py-2 rounded-lg">{error}</p>
              )}

              <div className="flex gap-3 pt-2">
                <Button type="submit" disabled={saving} className="flex items-center gap-2">
                  {saving ? (
                    <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Saving...</>
                  ) : (
                    <><Icon name="Save" size={16} /> {editingLink ? 'Update Link' : 'Create Link'}</>
                  )}
                </Button>
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="px-4 py-2 text-sm text-muted-foreground hover:text-foreground border border-border rounded-xl transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Links List */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : links?.length === 0 ? (
          <div className="text-center py-16 bg-card border border-border rounded-2xl">
            <div className="text-5xl mb-4">🎵</div>
            <h3 className="text-lg font-semibold text-foreground mb-2">No links yet</h3>
            <p className="text-muted-foreground text-sm mb-6">Create your first smart music link to start tracking.</p>
            <Button onClick={handleOpenCreate} className="flex items-center gap-2 mx-auto">
              <Icon name="Plus" size={16} />
              Create Link
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {links?.map(link => (
              <div key={link?.id} className="bg-card border border-border rounded-2xl p-5 flex flex-col sm:flex-row sm:items-center gap-4">
                {/* Cover */}
                <div className="flex-shrink-0">
                  {link?.cover_image_url ? (
                    <img src={link?.cover_image_url} alt={link?.title} className="w-14 h-14 rounded-xl object-cover" />
                  ) : (
                    <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-2xl">🎵</div>
                  )}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-foreground truncate">{link?.title}</h3>
                  {link?.artist_name && <p className="text-sm text-muted-foreground truncate">{link?.artist_name}</p>}
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-xs text-primary font-mono truncate">{BASE_URL}{link?.campaign_slug}</span>
                  </div>
                  {/* Platform indicators */}
                  <div className="flex gap-1.5 mt-2">
                    {link?.spotify_url && <span className="text-xs bg-[#1DB954]/10 text-[#1DB954] px-2 py-0.5 rounded-full">Spotify</span>}
                    {link?.apple_music_url && <span className="text-xs bg-red-500/10 text-red-500 px-2 py-0.5 rounded-full">Apple</span>}
                    {link?.youtube_music_url && <span className="text-xs bg-red-600/10 text-red-600 px-2 py-0.5 rounded-full">YouTube</span>}
                    {link?.tiktok_sound_url && <span className="text-xs bg-gray-500/10 text-gray-500 px-2 py-0.5 rounded-full">TikTok</span>}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button
                    onClick={() => handleCopy(link?.campaign_slug)}
                    title="Copy link"
                    className="p-2 rounded-lg hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
                  >
                    {copiedId === link?.campaign_slug ? <Icon name="Check" size={16} className="text-green-500" /> : <Icon name="Copy" size={16} />}
                  </button>
                  <a
                    href={`/go/${link?.campaign_slug}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Preview link"
                    className="p-2 rounded-lg hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
                  >
                    <Icon name="ExternalLink" size={16} />
                  </a>
                  <button
                    onClick={() => handleOpenEdit(link)}
                    title="Edit"
                    className="p-2 rounded-lg hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
                  >
                    <Icon name="Pencil" size={16} />
                  </button>
                  {deleteConfirm === link?.id ? (
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => handleDelete(link?.id)}
                        className="text-xs text-red-500 hover:text-red-600 font-medium px-2 py-1 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                      >
                        Delete
                      </button>
                      <button
                        onClick={() => setDeleteConfirm(null)}
                        className="text-xs text-muted-foreground hover:text-foreground px-2 py-1 rounded-lg hover:bg-muted transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setDeleteConfirm(link?.id)}
                      title="Delete"
                      className="p-2 rounded-lg hover:bg-muted transition-colors text-muted-foreground hover:text-red-500"
                    >
                      <Icon name="Trash2" size={16} />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
      <MobileNav />
    </div>
  );
};

export default CampaignLinkManager;
